import { useEffect, useState, useRef } from "react";
import AsyncSelect from "react-select/async";
import { Controller, useFormContext } from "react-hook-form";

const InputSelect = ({ props, data, handleOnLoadData, components }) => {
  const { control, setValue } = useFormContext();
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [defaultOption, setDefaultOption] = useState(null);

  // Track apakah defaultValue sudah pernah di-set ke form
  const hasSetInitialValue = useRef(false);

  const loadOptions = async (inputValue, callback) => {
    setIsLoading(true);
    try {
      const fetchedOptions = await handleOnLoadData(inputValue);
      setOptions((prevOptions) => {
        const newOptions = fetchedOptions || [];
        // Pastikan tidak menambahkan opsi yang sudah ada
        return [
          ...prevOptions,
          ...newOptions.filter(
            (opt) => !prevOptions.some((o) => o.value === opt.value),
          ),
        ];
      });
      callback(fetchedOptions || []);
    } catch (error) {
      console.error("Error loading options:", error);
      callback([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (data.defaultValue) {
      // 1. Cek apakah defaultValue adalah array (multiple) atau objek tunggal
      const isMulti = data.isMulti && Array.isArray(data.defaultValue);

      if (isMulti) {
        // Jika multiple choice
        const normalizedOptions = data.defaultValue.map((item) => ({
          value: String(item.value || item.id),
          label: item.label || item.text,
          ...item,
        }));

        // Simpan ke daftar opsi agar react-select bisa merendernya
        setOptions((prev) => {
          const combined = [...prev, ...normalizedOptions];
          // Filter unik agar tidak double
          return combined.filter(
            (v, i, a) => a.findIndex((t) => t.value === v.value) === i,
          );
        });

        // PERBAIKAN: Set value HANYA jika belum pernah di-set
        if (!hasSetInitialValue.current) {
          setValue(
            data.name,
            normalizedOptions.map((opt) => opt.value),
          );
          hasSetInitialValue.current = true;
        }
      } else {
        // 2. Logic lama untuk Single Choice (Tetap dipertahankan)
        const val = data.defaultValue.value || data.defaultValue.id;
        const lab = data.defaultValue.label || data.defaultValue.text;

        if (val) {
          const option = {
            ...data.defaultValue,
            value: String(val),
            label: lab,
          };
          setOptions((prev) => {
            if (!prev.find((o) => o.value === option.value)) {
              return [...prev, option];
            }
            return prev;
          });
          setDefaultOption(option);

          // PERBAIKAN: Set value HANYA jika belum pernah di-set
          if (!hasSetInitialValue.current) {
            setValue(data.name, option.value);
            hasSetInitialValue.current = true;
          }
        }
      }
    } else {
      // PERBAIKAN: Ketika defaultValue menjadi null (clear cascading), reset defaultOption
      setDefaultOption(null);
      // Reset flag agar bisa set initial value lagi di masa depan jika needed
      hasSetInitialValue.current = false;
    }
  }, [data.defaultValue, data.name, setValue, data.isMulti]);

  const handleChange = (selectedOption) => {
    if (Array.isArray(selectedOption)) {
      // Multi select -> simpan array value
      setValue(
        data.name,
        selectedOption.map((option) => option.value),
      );
    } else {
      // Single select -> simpan satu value
      setValue(data.name, selectedOption ? selectedOption.value : null);
    }

    setDefaultOption(selectedOption); // simpan objek selectedOption (bisa single atau array)

    // PERBAIKAN KRUSIAL: Panggil onChange dari parent (SettingsPage) jika ada
    if (props?.onChange) {
      props.onChange(selectedOption);
    }
  };

  const handleResetLogic = (currentValue) => {
    if (currentValue.value == null) {
      setDefaultOption(null);
    }
  };

  return (
    <Controller
      name={data.name}
      control={control}
      rules={{ required: data.rules?.required }}
      render={({ field }) => {
        handleResetLogic(field);

        return (
          <AsyncSelect
            {...field}
            className={data.className}
            placeholder={data.placeholder}
            loadOptions={loadOptions}
            isMulti={data.isMulti || false}
            isDisabled={data.disabled || false}
            isSearchable={true}
            menuPortalTarget={document.body}
            isClearable={true}
            cacheOptions
            noOptionsMessage={() => (isLoading ? "Loading..." : "No Data")}
            loadingMessage={() => "Loading options..."}
            defaultOptions={options.length > 0 ? options : false}
            onMenuOpen={() =>
              loadOptions("", (opts) =>
                setOptions((prev) => [
                  ...prev,
                  ...opts.filter(
                    (opt) => !prev.some((o) => o.value === opt.value),
                  ),
                ]),
              )
            }
            components={components}
            onChange={handleChange}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
            value={
              data.isMulti
                ? field.value && field.value.length > 0
                  ? options.filter((opt) => field.value.includes(opt.value))
                  : defaultOption || []
                : field.value !== null && field.value !== undefined
                  ? options.find((opt) => opt.value === field.value) ||
                    defaultOption
                  : null
            }
            key={`${data.name}-${field.value}-${data.defaultValue ? "has-default" : "no-default"}`}
          />
        );
      }}
    />
  );
};

export default InputSelect;
